import { DEFAULT_TIMEZONE } from '@yoyoclub/shared';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AdminLayout, { type NavItem } from '../../components/admin/AdminLayout';
import { TOUR, tourAttr } from '../../features/hostTour';
import { useAuth } from '../../hooks/useAuth';
import { bilingualLinesJoined, localize } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import { courseApi, type HostCourseSummary, type HostVideo } from '../../services/courseApi';

interface ActivityOption {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  sessions: Array<{
    id: string;
    startTime: string;
    endTime: string;
  }>;
}

const hostNavItems: NavItem[] = [
  { to: '/host/activities', label: 'Activities' },
  { to: '/host/courses', label: 'Courses' },
  { to: '/host/passes', label: 'Passes' },
  { to: '/host/bookings', label: 'Bookings' },
  { to: '/host/email', label: 'Email' },
];

interface Booking {
  id: string;
  type?: 'booking' | 'pass_purchase';
  activityTitle: string;
  activityTitleCN?: string | null;
  activityTitleEN?: string | null;
  passTitleCN?: string | null;
  passTitleEN?: string | null;
  activitySlug: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  sessionDate: string;
  sessionTime: string;
  participantsCount: number;
  totalAmount: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  photoConsent: 'FULL' | 'BLURRED' | 'NO_PHOTO' | null;
  utmSource: string | null;
  participants: Array<{
    name: string;
    type: string;
    age?: number;
  }>;
  passInfo?: {
    totalSessions: number;
    usedSessions: number;
    remainingSessions: number;
    paymentAmount: number;
    storeCreditUsed: number;
    expiresAt: string;
  };
}

interface UtmStat {
  source: string | null;
  count: number;
  revenue: number;
}

/** Activity (and pass) title in the viewer's language order (PRD §5.2). */
function bookingTitleLines(b: Booking, lang: string): [string, string] {
  const pairs: Array<[string | null | undefined, string | null | undefined]> = [
    [b.activityTitleCN, b.activityTitleEN],
  ];
  if (b.type === 'pass_purchase') pairs.push([b.passTitleCN, b.passTitleEN]);
  return bilingualLinesJoined(pairs, lang, b.activityTitle);
}

export default function AdminBookings() {
  const navigate = useNavigate();
  const location = useLocation();
  const siteLanguage = useSiteLanguage();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();

  const isHostContext = location.pathname.startsWith('/host/');
  const apiPrefix = isHostContext ? 'host' : 'admin';

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'confirmed' | 'cancelled' | 'transferred'>('all');
  const [utmFilter, setUtmFilter] = useState<string>('all');
  const [utmStats, setUtmStats] = useState<UtmStat[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchBookings = async () => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

        // Build URL with UTM filter
        let url = `${apiUrl}/${apiPrefix}/bookings`;
        if (!isHostContext && utmFilter !== 'all') {
          url += `?utmSource=${encodeURIComponent(utmFilter)}`;
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch bookings');
        }

        const result = await response.json();
        if (result.success) {
          setBookings(result.data || []);
          setUtmStats(result.utmStats || []);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load bookings';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [isAuthenticated, getToken, utmFilter, apiPrefix, isHostContext]);

  const filteredBookings = bookings.filter((booking) => {
    if (filter === 'all') return true;
    const s = booking.status.toLowerCase();
    if (filter === 'cancelled') return s.startsWith('cancelled') || s === 'refunded';
    return s === filter;
  });

  const handleExportCSV = async (filters?: {
    startDate?: string;
    endDate?: string;
    dateFilterType?: string;
    activityId?: string;
    sessionId?: string;
  }) => {
    try {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

      const params = new URLSearchParams();
      if (!isHostContext && utmFilter !== 'all') params.set('utmSource', utmFilter);
      if (filters?.startDate) params.set('startDate', filters.startDate);
      if (filters?.endDate) params.set('endDate', filters.endDate);
      if (filters?.dateFilterType) params.set('dateFilterType', filters.dateFilterType);
      if (filters?.activityId) params.set('activityId', filters.activityId);
      if (filters?.sessionId) params.set('sessionId', filters.sessionId);
      const qs = params.toString();

      const response = await fetch(`${apiUrl}/${apiPrefix}/bookings/export${qs ? `?${qs}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to export');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const localDate = new Date().toLocaleDateString('en-CA');
      a.download = match ? match[1] : `bookings-${localDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export bookings';
      setError(errorMessage);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout
      title="Booking Management"
      navItems={isHostContext ? hostNavItems : undefined}
      headerRight={
        <button
          onClick={() => setShowExportModal(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          {...tourAttr(TOUR.bookingsExport)}
        >
          Export CSV
        </button>
      }
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* UTM Stats Summary - admin only */}
      {!isHostContext && utmStats.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm mb-6 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">UTM Stats (Confirmed Orders)</h3>
          <div className="flex flex-wrap gap-3">
            {[...utmStats]
              .sort((a, b) => b.count - a.count)
              .map((stat) => (
                <button
                  key={stat.source || '__null__'}
                  onClick={() => setUtmFilter(stat.source || '__null__')}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    utmFilter === (stat.source || '__null__')
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="font-medium">{stat.source || 'Direct'}</span>
                  <span className="ml-2 opacity-75">
                    {stat.count} · ${(stat.revenue / 100).toLocaleString()}
                  </span>
                </button>
              ))}
            {utmFilter !== 'all' && (
              <button
                onClick={() => setUtmFilter('all')}
                className="px-3 py-2 rounded-lg text-sm bg-gray-200 text-gray-600 hover:bg-gray-300"
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div>
          <nav className="flex -mb-px overflow-x-auto" {...tourAttr(TOUR.bookingsTabs)}>
            {(['all', 'confirmed', 'cancelled', 'transferred'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap flex-shrink-0 ${
                  filter === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                  {tab === 'all'
                    ? bookings.length
                    : tab === 'cancelled'
                      ? bookings.filter((b) => {
                          const s = b.status.toLowerCase();
                          return s.startsWith('cancelled') || s === 'refunded';
                        }).length
                      : bookings.filter((b) => b.status.toLowerCase() === tab).length}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Bookings Table */}
      <div
        className="bg-white rounded-lg shadow-sm overflow-hidden"
        {...tourAttr(TOUR.bookingsTable)}
      >
        {filteredBookings.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <div className="text-4xl mb-4">📋</div>
            <p>No bookings found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Participants
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    {...tourAttr(TOUR.bookingsColConsent)}
                  >
                    Photo Consent
                  </th>
                  {!isHostContext && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      UTM Source
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBookings.map((booking) => {
                  const [titleTop, titleBottom] = bookingTitleLines(booking, siteLanguage);
                  return (
                    <tr key={booking.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 max-w-[200px] truncate">
                          {booking.type === 'pass_purchase' && (
                            <span className="inline-flex px-1.5 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800 mr-1">
                              Pass
                            </span>
                          )}
                          {titleTop}
                        </div>
                        {titleBottom && (
                          <div className="text-xs text-gray-500 max-w-[200px] truncate">
                            {titleBottom}
                          </div>
                        )}
                        <div className="text-xs text-gray-500">
                          {new Date(booking.createdAt).toLocaleString('en-US', {
                            timeZone: DEFAULT_TIMEZONE,
                            month: 'numeric',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{booking.customerName}</div>
                        {!isHostContext && (
                          <div className="text-xs text-gray-500">{booking.customerEmail}</div>
                        )}
                        {booking.customerPhone && (
                          <div className="text-xs text-gray-500">{booking.customerPhone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(booking.sessionDate).toLocaleDateString('en-US', {
                            timeZone: DEFAULT_TIMEZONE,
                          })}
                        </div>
                        <div className="text-xs text-gray-500">{booking.sessionTime}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {booking.type === 'pass_purchase' ? (
                          <button
                            onClick={() => setSelectedBooking(booking)}
                            className="text-sm text-purple-600 hover:underline"
                          >
                            {booking.passInfo?.usedSessions}/{booking.passInfo?.totalSessions} used
                          </button>
                        ) : (
                          <button
                            onClick={() => setSelectedBooking(booking)}
                            className="text-sm text-primary hover:underline"
                          >
                            {booking.participantsCount} participant
                            {booking.participantsCount > 1 ? 's' : ''}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${(booking.totalAmount / 100).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={booking.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <PhotoConsentBadge consent={booking.photoConsent} />
                      </td>
                      {!isHostContext && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {booking.utmSource ? (
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                              {booking.utmSource}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Direct</span>
                          )}
                        </td>
                      )}
                      <td
                        className="px-6 py-4 whitespace-nowrap"
                        {...tourAttr(TOUR.bookingsRowActions)}
                      >
                        <button
                          onClick={() => setSelectedBooking(booking)}
                          className="text-xs text-gray-600 hover:text-gray-900"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          isHostContext={isHostContext}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          onClose={() => setShowExportModal(false)}
          onExport={(filters) => {
            handleExportCSV(filters);
            setShowExportModal(false);
          }}
          getToken={getToken}
          apiPrefix={apiPrefix}
        />
      )}
    </AdminLayout>
  );
}

function ExportModal({
  onClose,
  onExport,
  getToken,
  apiPrefix,
}: {
  onClose: () => void;
  onExport: (filters: {
    startDate?: string;
    endDate?: string;
    dateFilterType?: string;
    activityId?: string;
    sessionId?: string;
  }) => void;
  getToken: () => Promise<string | undefined>;
  apiPrefix: string;
}) {
  const siteLanguage = useSiteLanguage();
  const [tab, setTab] = useState<'bookings' | 'courses'>('bookings');
  // The course tab mounts on first visit and then stays mounted (just hidden),
  // so switching back and forth keeps each tab's filters.
  const [coursesVisited, setCoursesVisited] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Host exports always filter by session date (matches the settlement sheet);
  // only admins get the Booking Date / Session Date toggle.
  const isHostContext = apiPrefix === 'host';
  const [dateFilterType, setDateFilterType] = useState<'booking' | 'session'>(
    isHostContext ? 'session' : 'booking'
  );
  const [activities, setActivities] = useState<ActivityOption[]>([]);
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [loadingActivities, setLoadingActivities] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${apiUrl}/${apiPrefix}/activities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setActivities(
              (result.data || []).map((a: ActivityOption) => ({
                id: a.id,
                titleCN: a.titleCN,
                sessions: a.sessions || [],
              }))
            );
          }
        }
      } catch {
        // silently fail — dropdown will just be empty
      } finally {
        setLoadingActivities(false);
      }
    };
    fetchActivities();
  }, [getToken, apiPrefix]);

  const selectedActivity = activities.find((a) => a.id === selectedActivityId);
  const sessionOptions = selectedActivity?.sessions || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Export Data</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ✕
            </button>
          </div>

          {/* Data set: in-person bookings vs recorded-course sales */}
          <div
            className="flex rounded-lg border border-gray-300 overflow-hidden mb-4"
            {...tourAttr(TOUR.bxDataset)}
          >
            <button
              type="button"
              onClick={() => setTab('bookings')}
              className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                tab === 'bookings'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Bookings
            </button>
            <button
              type="button"
              onClick={() => {
                setTab('courses');
                setCoursesVisited(true);
              }}
              className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                tab === 'courses'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Course Sales
            </button>
          </div>

          {coursesVisited && (
            <div className={tab === 'courses' ? '' : 'hidden'}>
              <CourseSalesExportTab getToken={getToken} onClose={onClose} />
            </div>
          )}

          {tab === 'courses' ? null : (
            <div className="space-y-4" {...tourAttr(TOUR.bxFilters)}>
              {/* Date Filter Type */}
              {!isHostContext && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Filter By</label>
                  <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setDateFilterType('booking')}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                        dateFilterType === 'booking'
                          ? 'bg-primary text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Booking Date
                    </button>
                    <button
                      type="button"
                      onClick={() => setDateFilterType('session')}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                        dateFilterType === 'session'
                          ? 'bg-primary text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Session Date
                    </button>
                  </div>
                </div>
              )}

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isHostContext ? 'Session Date Range' : 'Date Range'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Activity Select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Activity</label>
                <select
                  value={selectedActivityId}
                  onChange={(e) => {
                    setSelectedActivityId(e.target.value);
                    setSelectedSessionId('');
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={loadingActivities}
                >
                  <option value="">All Activities</option>
                  {activities.map((a) => (
                    <option key={a.id} value={a.id}>
                      {localize(a.titleCN, a.titleEN, siteLanguage)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Session Select */}
              {selectedActivityId && sessionOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session</label>
                  <select
                    value={selectedSessionId}
                    onChange={(e) => setSelectedSessionId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">All Sessions</option>
                    {sessionOptions
                      .sort(
                        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
                      )
                      .map((s) => {
                        const start = new Date(s.startTime);
                        const end = new Date(s.endTime);
                        const dateStr = start.toLocaleDateString('en-US', {
                          timeZone: DEFAULT_TIMEZONE,
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        });
                        const timeStr = `${start.toLocaleTimeString('en-US', {
                          timeZone: DEFAULT_TIMEZONE,
                          hour: 'numeric',
                          minute: '2-digit',
                        })} - ${end.toLocaleTimeString('en-US', {
                          timeZone: DEFAULT_TIMEZONE,
                          hour: 'numeric',
                          minute: '2-digit',
                        })}`;
                        return (
                          <option key={s.id} value={s.id}>
                            {dateStr} · {timeStr}
                          </option>
                        );
                      })}
                  </select>
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    onExport({
                      startDate: startDate || undefined,
                      endDate: endDate || undefined,
                      dateFilterType,
                      activityId: selectedActivityId || undefined,
                      sessionId: selectedSessionId || undefined,
                    })
                  }
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Export
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Recorded-course sales export. Purchases have a single time axis (the purchase
// date), so there is no Filter By toggle here.
function CourseSalesExportTab({
  getToken,
  onClose,
}: {
  getToken: () => Promise<string | undefined>;
  onClose: () => void;
}) {
  const siteLanguage = useSiteLanguage();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [courses, setCourses] = useState<HostCourseSummary[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [lessons, setLessons] = useState<HostVideo[]>([]);
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        if (!token) return;
        setCourses(await courseApi.hostListCourses(token));
      } catch {
        // silently fail — dropdown will just be empty
      } finally {
        setLoadingCourses(false);
      }
    };
    load();
  }, [getToken]);

  // Lessons are only needed once a course is picked; superseded versions are
  // dropped so the list shows the lessons buyers currently see.
  useEffect(() => {
    setSelectedVideoId('');
    setLessons([]);
    if (!selectedCourseId) return;

    let cancelled = false;
    const load = async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const { videos } = await courseApi.hostListVideos(selectedCourseId, token);
        if (cancelled) return;
        setLessons(videos.filter((v) => v.supersededAt === null).sort((a, b) => a.order - b.order));
      } catch {
        // silently fail — the lesson dropdown stays hidden
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedCourseId, getToken]);

  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const { blob, filename } = await courseApi.hostExportCourseSales(
        {
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          activityId: selectedCourseId || undefined,
          videoId: selectedVideoId || undefined,
        },
        token
      );

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export course sales');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Start</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">End</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
        <select
          value={selectedCourseId}
          onChange={(e) => setSelectedCourseId(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          disabled={loadingCourses}
        >
          <option value="">All Courses</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {localize(c.titleCN, c.titleEN, siteLanguage)}
            </option>
          ))}
        </select>
      </div>

      {selectedCourseId && lessons.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Lesson</label>
          <select
            value={selectedVideoId}
            onChange={(e) => setSelectedVideoId(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Lessons</option>
            {lessons.map((v) => (
              <option key={v.id} value={v.id}>
                Lesson {v.order + 1} · {localize(v.titleCN, v.titleEN, siteLanguage)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          {exporting ? 'Exporting...' : 'Export'}
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    CONFIRMED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Confirmed' },
    CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
    CANCELLED_CREDIT: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled (Credit)' },
    CANCELLED_REFUND: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled (Refund)' },
    CANCELLED_PASS: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled (Pass)' },
    CANCELLED_BY_HOST: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Awaiting Refund' },
    REFUNDED: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Refunded' },
    TRANSFERRED: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Transferred' },
  };

  const config = statusConfig[status] || {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    label: status,
  };

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
}

function PhotoConsentBadge({ consent }: { consent: 'FULL' | 'BLURRED' | 'NO_PHOTO' | null }) {
  const consentConfig: Record<string, { bg: string; text: string; label: string }> = {
    FULL: { bg: 'bg-green-100', text: 'text-green-800', label: 'Full' },
    BLURRED: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Blurred' },
    NO_PHOTO: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'No Photo' },
  };

  if (!consent) {
    return <span className="text-xs text-gray-400">Not Set</span>;
  }

  const config = consentConfig[consent];

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
}

function BookingDetailModal({
  booking,
  onClose,
  isHostContext,
}: {
  booking: Booking;
  onClose: () => void;
  isHostContext?: boolean;
}) {
  const siteLanguage = useSiteLanguage();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Booking Details</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ✕
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-500">Booking ID</label>
              <p className="font-mono text-sm">{booking.id}</p>
            </div>

            <div>
              <label className="text-sm text-gray-500">Activity</label>
              <p className="font-medium">{bookingTitleLines(booking, siteLanguage)[0]}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Session Date</label>
                <p>
                  {new Date(booking.sessionDate).toLocaleDateString('en-US', {
                    timeZone: DEFAULT_TIMEZONE,
                  })}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Time</label>
                <p>{booking.sessionTime}</p>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-500">Customer</label>
              <p className="font-medium">{booking.customerName}</p>
              {!isHostContext && <p className="text-sm text-gray-600">{booking.customerEmail}</p>}
              {booking.customerPhone && (
                <p className="text-sm text-gray-600">{booking.customerPhone}</p>
              )}
            </div>

            {booking.type === 'pass_purchase' && booking.passInfo ? (
              <div>
                <label className="text-sm text-gray-500">Pass Details</label>
                <div className="mt-2 space-y-2 bg-purple-50 rounded-lg p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Sessions</span>
                    <span className="font-medium">{booking.passInfo.totalSessions}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Used</span>
                    <span className="font-medium">{booking.passInfo.usedSessions}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Remaining</span>
                    <span className="font-medium">{booking.passInfo.remainingSessions}</span>
                  </div>
                  {booking.passInfo.storeCreditUsed > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Store Credit Used</span>
                      <span className="font-medium">
                        ${(booking.passInfo.storeCreditUsed / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Expires</span>
                    <span className="font-medium">
                      {new Date(booking.passInfo.expiresAt).toLocaleDateString('en-US', {
                        timeZone: DEFAULT_TIMEZONE,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <label className="text-sm text-gray-500">
                  Participants ({booking.participantsCount})
                </label>
                <div className="mt-2 space-y-1">
                  {booking.participants?.map((participant, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                        {participant.type}
                      </span>
                      <span>{participant.name}</span>
                      {participant.type === 'child' && participant.age !== undefined && (
                        <span className="text-gray-500">({participant.age} yrs)</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Amount</label>
                <p className="text-lg font-semibold text-green-600">
                  ${(booking.totalAmount / 100).toFixed(2)}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Status</label>
                <div className="mt-1">
                  <StatusBadge status={booking.status} />
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-500">Photo Consent</label>
              <div className="mt-1">
                <PhotoConsentBadge consent={booking.photoConsent} />
              </div>
            </div>

            {!isHostContext && (
              <div>
                <label className="text-sm text-gray-500">UTM Source</label>
                <p className="mt-1">
                  {booking.utmSource ? (
                    <span className="text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded">
                      {booking.utmSource}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">Direct</span>
                  )}
                </p>
              </div>
            )}

            <div>
              <label className="text-sm text-gray-500">Booked On</label>
              <p>
                {new Date(booking.createdAt).toLocaleString('en-US', {
                  timeZone: DEFAULT_TIMEZONE,
                })}
              </p>
            </div>
          </div>

          <div className="mt-6 pt-4">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
